import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DemoRequestDialog from '../components/DemoRequestDialog';
import ExpertServicesDialog from '../components/ExpertServicesDialog';
import ProductOfBrand from '../components/shared/ProductOfBrand';
import SiteFooter from '../components/shared/SiteFooter';
import { useAuth } from '../contexts/AuthContext';
import { FREE_SIGNUP_ENABLED } from '../constants/freeSignup';
import { INK, MUTED, PAPER, LINE, goldButtonSx } from '../constants/brand';

const capabilities = [
  {
    title: 'Search the public record',
    description: 'Filter wells, production, inspections, violations, severances, and spills for the assets you are screening.',
  },
  {
    title: 'Flag what needs a look',
    description: 'Pre-screen regulatory issues so the review starts with the leases that actually need attention.',
  },
  {
    title: 'Read it in one place',
    description: 'Maps, charts, and tables sit with the search, so the screening does not live in a stack of spreadsheets.',
  },
  {
    title: 'Leave with a report',
    description: 'Generate a desktop diligence report in less than 15 minutes, at a fraction of a manual third-party review.',
  },
];

const steps = [
  {
    n: '01',
    title: 'Set the search',
    description: 'Name the operator, lease, or area. Diligence Desk pulls the regulatory record for that set.',
  },
  {
    n: '02',
    title: 'Screen the results',
    description: 'Use flags, maps, and analytics to see where compliance and operating issues concentrate.',
  },
  {
    n: '03',
    title: 'Decide the next step',
    description: 'Export the report, or hand the same picture to the ESG Dynamics team for a deeper review.',
  },
];

const expertServices = [
  {
    title: 'Expert analysis',
    items: [
      'Additional insights and project support',
      'Exclusive ESG Dynamics data access',
      'Written analysis and conclusions',
      'Recommendations you can act on',
    ],
  },
  {
    title: 'Field work',
    items: [
      'Phase 1 Environmental Site Assessments',
      'On-site physical assessments',
      'Regulatory compliance support',
      'Project consulting and implementation',
    ],
  },
];

function SectionRule() {
  return (
    <Box
      sx={{
        width: 36,
        height: 3,
        bgcolor: 'secondary.main',
        borderRadius: 999,
        mb: 2.5,
      }}
    />
  );
}

const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [expertServicesDialogOpen, setExpertServicesDialogOpen] = useState(false);

  const goToApp = () => navigate(isAuthenticated ? '/app' : '/login');

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fff', color: INK }}>
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          bgcolor: 'rgba(255,255,255,0.94)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            minHeight: 72,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer', minWidth: 0 }}
          >
            <img src="/shield_logo.png" alt="" style={{ height: 34, width: 'auto' }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 750, color: theme.palette.primary.main, lineHeight: 1.1, fontSize: { xs: '0.98rem', sm: '1.05rem' }, whiteSpace: 'nowrap' }}>
                Diligence Desk
              </Typography>
              <ProductOfBrand
                variant="caption"
                sx={{ lineHeight: 1.2, display: { xs: 'none', sm: 'block' } }}
                onClick={(e) => e.stopPropagation()}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            <Button
              onClick={() => scrollTo('product')}
              sx={{ display: { xs: 'none', md: 'inline-flex' }, color: INK, textTransform: 'none', fontWeight: 600 }}
            >
              The desk
            </Button>
            <Button
              onClick={() => scrollTo('experts')}
              sx={{ display: { xs: 'none', md: 'inline-flex' }, color: INK, textTransform: 'none', fontWeight: 600 }}
            >
              Experts
            </Button>
            <Button
              onClick={goToApp}
              sx={{ color: theme.palette.primary.main, textTransform: 'none', fontWeight: 650, minWidth: 0, px: { xs: 1, sm: 2 }, whiteSpace: 'nowrap' }}
            >
              {isAuthenticated ? 'Dashboard' : 'Sign in'}
            </Button>
            <Button variant="contained" onClick={() => setDemoDialogOpen(true)} sx={{ ...goldButtonSx, px: { xs: 1.75, sm: 2.75 }, whiteSpace: 'nowrap' }}>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Request </Box>
              Demo
            </Button>
          </Box>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: PAPER }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1.05fr' },
              gap: { xs: 5, md: 7 },
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontSize: '0.78rem',
                  mb: 2,
                }}
              >
                Desktop diligence
              </Typography>
              <Typography
                component="h1"
                sx={{
                  fontWeight: 750,
                  fontSize: { xs: '2.4rem', sm: '3.1rem', md: '3.6rem' },
                  lineHeight: 1.02,
                  letterSpacing: '-0.03em',
                  mb: 2.5,
                  color: INK,
                }}
              >
                Oil & gas diligence, simplified.
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.6, maxWidth: 520, mb: 3.5 }}>
                Environmental and regulatory screening for oil and gas assets. Search the public record, flag the issues, and produce a desktop report in less than 15 minutes.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                {FREE_SIGNUP_ENABLED && (
                  <Button
                    variant="contained"
                    onClick={() => navigate('/register')}
                    sx={{ ...goldButtonSx, px: 3, py: 1.25 }}
                  >
                    Get free access
                  </Button>
                )}
                <Button
                  variant={FREE_SIGNUP_ENABLED ? 'outlined' : 'contained'}
                  onClick={() => setDemoDialogOpen(true)}
                  sx={FREE_SIGNUP_ENABLED ? {
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 999,
                    px: 3,
                    py: 1.25,
                    borderColor: INK,
                    color: INK,
                  } : { ...goldButtonSx, px: 3, py: 1.25 }}
                >
                  Request a demo
                </Button>
              </Box>
              <Typography sx={{ color: MUTED, fontSize: '0.92rem', maxWidth: 460 }}>
                {FREE_SIGNUP_ENABLED
                  ? 'Start on the introductory tier, then move to Pro when you need the full desk.'
                  : 'Free account signup is temporarily unavailable. Request a demo and we will help you get access.'}
              </Typography>
            </Box>

            <Box>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: INK,
                  boxShadow: '0 24px 50px rgba(23, 32, 51, 0.16)',
                  aspectRatio: '16 / 9',
                }}
              >
                <iframe
                  src="https://player.vimeo.com/video/1149233849?badge=0&autopause=0&player_id=0&app_id=58479&title=0&byline=0&portrait=0&dnt=1&transparent=0&loop=0&autoplay=0"
                  title="Diligence Desk Demo"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                />
              </Box>
              <Typography sx={{ mt: 1.5, color: MUTED, fontSize: '0.85rem' }}>
                A short look at a search, the flags, and the report.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Box sx={{ borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}`, bgcolor: '#fff' }}>
        <Container
          maxWidth="lg"
          sx={{
            py: 3,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: { xs: 2.5, sm: 3 },
          }}
        >
          {[
            ['< 15 min', 'From search to a desktop report'],
            ['> 90%', 'Less time spent on the first screen'],
            ['One desk', 'Search, maps, flags, and the report'],
          ].map(([stat, label]) => (
            <Box key={stat}>
              <Typography sx={{ fontWeight: 750, fontSize: '1.55rem', letterSpacing: '-0.03em', color: theme.palette.primary.main }}>
                {stat}
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: '0.95rem' }}>{label}</Typography>
            </Box>
          ))}
        </Container>
      </Box>

      <Box id="product" component="section" sx={{ scrollMarginTop: '88px', py: { xs: 8, md: 11 } }}>
        <Container maxWidth="lg">
          <SectionRule />
          <Typography component="h2" sx={{ fontWeight: 750, fontSize: { xs: '2rem', md: '2.6rem' }, letterSpacing: '-0.03em', mb: 1.5 }}>
            Built for the first pass.
          </Typography>
          <Typography sx={{ color: MUTED, maxWidth: 560, mb: 6, fontSize: '1.05rem', lineHeight: 1.6 }}>
            Diligence Desk is the self-serve screen. Use it before a data room is open, or before you send a team to the field.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 0,
              borderTop: `1px solid ${LINE}`,
            }}
          >
            {capabilities.map((item, index) => (
              <Box
                key={item.title}
                sx={{
                  py: 3.5,
                  pr: { md: index % 2 === 0 ? 5 : 0 },
                  pl: { md: index % 2 === 1 ? 5 : 0 },
                  borderBottom: `1px solid ${LINE}`,
                  borderLeft: { md: index % 2 === 1 ? `1px solid ${LINE}` : 'none' },
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', mb: 1 }}>{item.title}</Typography>
                <Typography sx={{ color: MUTED, lineHeight: 1.6 }}>{item.description}</Typography>
              </Box>
            ))}
          </Box>

          <Typography component="h3" sx={{ fontWeight: 750, fontSize: { xs: '1.6rem', md: '1.9rem' }, letterSpacing: '-0.02em', mt: 8, mb: 4 }}>
            How a screen runs
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: { xs: 3, md: 4 } }}>
            {steps.map((step) => (
              <Box key={step.n}>
                <Typography sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.04em', mb: 1 }}>
                  {step.n}
                </Typography>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>{step.title}</Typography>
                <Typography sx={{ color: MUTED, lineHeight: 1.6 }}>{step.description}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <Box id="experts" component="section" sx={{ scrollMarginTop: '88px', bgcolor: '#123044', color: '#fff', py: { xs: 8, md: 11 } }}>
        <Container maxWidth="lg">
          <SectionRule />
          <Typography component="h2" sx={{ fontWeight: 750, fontSize: { xs: '2rem', md: '2.6rem' }, letterSpacing: '-0.03em', maxWidth: 640, mb: 2 }}>
            When the desk is not enough, the team is.
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.78)', maxWidth: 620, mb: 6, fontSize: '1.05rem', lineHeight: 1.6 }}>
            ESG Dynamics is the firm behind Diligence Desk. Environmental and regulatory specialists can take the same assets further, in the office or in the field.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 4, md: 8 }, mb: 6 }}>
            {expertServices.map((group) => (
              <Box key={group.title}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', mb: 2 }}>{group.title}</Typography>
                {group.items.map((item) => (
                  <Typography key={item} sx={{ color: 'rgba(255,255,255,0.86)', py: 0.7, borderTop: '1px solid rgba(255,255,255,0.14)' }}>
                    {item}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>

          <Button
            variant="contained"
            onClick={() => setExpertServicesDialogOpen(true)}
            sx={goldButtonSx}
          >
            Talk with the team
          </Button>
          <Typography sx={{ mt: 1.5, color: 'rgba(255,255,255,0.7)', fontSize: '0.92rem' }}>
            info@esg-dynamics.com
          </Typography>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 9 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography component="h2" sx={{ fontWeight: 750, fontSize: { xs: '1.8rem', md: '2.3rem' }, letterSpacing: '-0.03em', mb: 1.5 }}>
            See it on an asset you already know.
          </Typography>
          <Typography sx={{ color: MUTED, mb: 3, lineHeight: 1.6 }}>
            {FREE_SIGNUP_ENABLED
              ? 'Create an introductory account, or sit down with us for a walkthrough on your own search.'
              : 'Free online signup is paused. Request a demo and we will set up access for you.'}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={() => setDemoDialogOpen(true)} sx={{ ...goldButtonSx, px: 3, py: 1.25 }}>
              Request a demo
            </Button>
            {FREE_SIGNUP_ENABLED && (
              <Button
                variant="outlined"
                onClick={() => navigate('/register')}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999, px: 3, borderColor: INK, color: INK }}
              >
                Start with free access
              </Button>
            )}
          </Box>
        </Container>
      </Box>

      <SiteFooter />

      <DemoRequestDialog open={demoDialogOpen} onClose={() => setDemoDialogOpen(false)} />
      <ExpertServicesDialog open={expertServicesDialogOpen} onClose={() => setExpertServicesDialogOpen(false)} />
    </Box>
  );
};

export default LandingPage;
